/**
 * Client-side host detection. Decides whether the SPA is running as the platform
 * app (dashboard/admin/marketing) or as a seller store's public storefront, based
 * purely on the current hostname vs the configured platform apex.
 *
 * The apex comes from VITE_ROOT_DOMAIN when set, else the host of
 * VITE_APP_PUBLIC_URL (e.g. http://digital-embed.test -> digital-embed.test).
 * A `?__store=<sub>` query override forces storefront mode for local dev where a
 * wildcard host isn't convenient.
 */
import { hostMode, apexFromUrl, type HostMode } from "@shared/host";

/** The platform apex this build treats as "the app", e.g. "digital-embed.com". */
export function platformApex(): string {
  const explicit = import.meta.env.VITE_ROOT_DOMAIN as string | undefined;
  if (explicit && explicit.trim()) return explicit.trim().toLowerCase();
  const publicUrl = import.meta.env.VITE_APP_PUBLIC_URL as string | undefined;
  return publicUrl ? apexFromUrl(publicUrl) : "";
}

/**
 * A retired apex whose storefronts must keep rendering, e.g. "sellfy.win" after
 * the platform moved to sellhiz.win. Empty when there is no such domain.
 *
 * Storefront hosts on this apex are still classified as subdomain storefronts, so
 * they serve normally whether or not the Worker's retired-apex bridge is turned
 * on. Buyer auth is deliberately NOT included — `apexBuyerRedirect` keeps sending
 * login, reset and /access to the CURRENT apex, so sessions never split across two
 * origins.
 */
export function legacyApex(): string {
  const v = import.meta.env.VITE_LEGACY_ROOT_DOMAIN as string | undefined;
  return v && v.trim() ? v.trim().toLowerCase() : "";
}

/**
 * How the SPA should behave for the current host. `?__store=<sub>` forces a
 * subdomain storefront and `?__domain=<host>` forces a custom-domain storefront,
 * so both can be exercised on localhost during dev.
 */
export function currentHostMode(): HostMode {
  if (typeof window === "undefined") return { kind: "app" };
  const params = new URLSearchParams(window.location.search);
  const sub = params.get("__store");
  if (sub) return { kind: "subdomain", subdomain: sub.trim().toLowerCase() };
  const dom = params.get("__domain");
  if (dom) return { kind: "domain", host: dom.trim().toLowerCase() };
  return hostMode(window.location.hostname, platformApex(), [legacyApex()]);
}

/**
 * The store subdomain for the current request, or null when this is the app or a
 * custom-domain storefront. `?__store=<sub>` wins so it can run on localhost.
 */
export function currentSubdomain(): string | null {
  const mode = currentHostMode();
  return mode.kind === "subdomain" ? mode.subdomain : null;
}

/** True when the SPA is serving a seller storefront (subdomain or custom domain). */
export function isStorefrontHost(): boolean {
  return currentHostMode().kind !== "app";
}

/**
 * Validate a post-login `?next=` destination. It is only honoured as a plain
 * same-origin path.
 *
 * This param exists because the buyer login bounce crosses origins (storefront →
 * apex, see ApexRedirect), where router state cannot survive — which also makes
 * it attacker-reachable. Anything protocol-relative ("//evil.com"), backslash-
 * tricked ("/\\evil.com", which several browsers normalise to "//"), or absolute
 * would turn the login form into an open redirect, so all are rejected.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith("/")) return null;
  if (raw.startsWith("//") || raw.startsWith("/\\")) return null;
  return raw;
}

/** The public URL of a store's subdomain, for previews/links in the dashboard.
 *  Mirrors the current scheme; falls back to https for safety. */
export function storeUrl(subdomain: string): string {
  const apex = platformApex();
  const scheme =
    typeof window !== "undefined" && window.location.protocol === "http:" ? "http" : "https";
  return `${scheme}://${subdomain}.${apex}`;
}

/**
 * The public origin a store's pages are actually served from, for building share
 * links in the dashboard: a verified custom domain wins, else the platform
 * subdomain, else null (caller falls back to the apex app URL). Never returns the
 * apex here so callers keep their own fallback (env.appPublicUrl).
 */
export function storeOrigin(
  store: { subdomain?: string | null; custom_domain?: string | null; custom_domain_verified?: boolean } | null,
): string | null {
  if (!store) return null;
  const scheme =
    typeof window !== "undefined" && window.location.protocol === "http:" ? "http" : "https";
  if (store.custom_domain && store.custom_domain_verified) {
    return `${scheme}://${store.custom_domain}`;
  }
  if (store.subdomain) return storeUrl(store.subdomain);
  return null;
}
